import React, {type ReactNode} from 'react';
import {emotionStyled} from '@packages/utilities/styled';

import type {HTMLAttributes, SpringTooling, AsProps} from '../__SharedTypes';

type VisuallyHiddenProps = {
  children?: ReactNode;
} & AsProps &
  HTMLAttributes &
  SpringTooling;

export const visuallyHiddenStyles = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  margin: '0 -1px -1px 0',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  width: 1,
  whiteSpace: 'nowrap',
} as const;

const VisuallyHiddenView = emotionStyled('div')(
  {},
  {
    componentName: 'VisuallyHidden',
  }
);

/**
 * # VisuallyHidden
 *
 * VisuallyHidden hides its inner content from view, while keeping its content visible to screen readers.
 *
 * ## Usage
 *
 * ```jsx
 * import { VisuallyHidden } from "@packages/systems/spring";
 *
 * const Example = () => {
 *   return (
 *     <>
 *       <span>Totally visible.</span>
 *       <VisuallyHidden>
 *        <span>Hidden, but can be accessed by screen readers.</span>
 *       </VisuallyHidden>
 *     </>
 *   );
 * };
 * ```
 *
 * @component
 */

export const VisuallyHidden = React.forwardRef<
  HTMLDivElement,
  VisuallyHiddenProps
>(({style: styleProp, ...props}: VisuallyHiddenProps, ref) => {
  const styles = {
    ...visuallyHiddenStyles,
    ...styleProp,
  } as const;

  return <VisuallyHiddenView {...props} ref={ref} style={styles} />;
});

VisuallyHidden.displayName = 'VisuallyHidden';

export default VisuallyHidden;
